import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import Select from '../../../../components/ui/Select';
import { getAvailableFields } from '../../../../api/General/documentTemplates';

const FieldMappingStep = ({ data, onDataUpdate, onSaveDraft, onPublish, onPrev, busy }) => {
  const { t, i18n } = useTranslation();
  const [fieldMappings, setFieldMappings] = useState(data?.fieldMappings || []);
  const [unmappedFields, setUnmappedFields] = useState([]);
  // The mappable fields come from the server: built-in catalog (Employee / Company / General)
  // plus this company's HR-defined custom fields (group "Custom"). Ordered by group already.
  const [catalog, setCatalog] = useState([]);

  const isAr = i18n.language?.startsWith('ar');

  useEffect(() => {
    let active = true;
    getAvailableFields()
      .then((res) => { if (active && !res?.errors?.length) setCatalog(res.payload ?? []); })
      .catch(() => { /* leave catalog empty; saved mappings still render */ });
    return () => { active = false; };
  }, []);

  // Localized label for a field (custom fields carry their own Arabic label; built-ins use i18n).
  const fieldLabel = (f) => (f.isCustom ? (isAr && f.labelAr ? f.labelAr : f.label) : t(f.label));

  // Select options, and a lookup from value → field for the per-row group chip.
  const availableFields = useMemo(
    () => catalog.map((f) => ({ value: f.value, label: fieldLabel(f) })),
    [catalog, isAr]
  );
  const fieldByValue = useMemo(() => {
    const m = {};
    catalog.forEach((f) => { m[f.value] = f; });
    return m;
  }, [catalog]);

  const groupLabel = (group) => {
    switch (group) {
      case 'Employee': return t('Employee');
      case 'Company': return t('Company');
      case 'General': return t('General');
      case 'Custom': return t('Custom');
      default: return group;
    }
  };

  useEffect(() => {
    if (data?.fieldMappings && data.fieldMappings.length > 0) {
      setFieldMappings(data.fieldMappings);
      return;
    }
    if (data?.detectedFields && data?.detectedFields?.length > 0) {
      // Auto-map a placeholder when its normalized name matches a known field value. Re-runs
      // when the catalog arrives, but never overwrites a selection the user already made.
      setFieldMappings((prev) => {
        const prevByPlaceholder = {};
        (prev || []).forEach((fm) => { prevByPlaceholder[fm.placeholder] = fm; });
        return data.detectedFields.map((field) => {
          const existing = prevByPlaceholder[field];
          if (existing && existing.mappedTo) return existing;
          const normalizedField = field?.toLowerCase()?.replace(/[\s-]/g, '_');
          const matchedField = fieldByValue[normalizedField];
          return {
            placeholder: field,
            mappedTo: matchedField?.value || '',
            autoMapped: !!matchedField,
          };
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.detectedFields, fieldByValue]);

  useEffect(() => {
    setUnmappedFields(fieldMappings?.filter((fm) => !fm?.mappedTo));
  }, [fieldMappings]);

  const handleFieldMapping = (placeholder, mappedValue) => {
    setFieldMappings((prev) =>
      prev?.map((fm) => (fm?.placeholder === placeholder ? { ...fm, mappedTo: mappedValue, autoMapped: false } : fm))
    );
  };

  // Persist the current mappings to wizard state, then hand them to the save action
  // directly (avoids reading stale parent state immediately after a setState).
  const save = (publish) => {
    onDataUpdate('fieldMappings', fieldMappings);
    (publish ? onPublish : onSaveDraft)(fieldMappings);
  };

  // Publishing requires every placeholder mapped (the server enforces this too); a draft
  // can be saved at any time.
  const canPublish = fieldMappings?.length === 0 || fieldMappings?.every((fm) => fm?.mappedTo);
  const mappedCount = fieldMappings.length - unmappedFields.length;
  const progressPct = fieldMappings.length ? Math.round((mappedCount / fieldMappings.length) * 100) : 0;

  return (
    <div className="p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{t('Map Template Fields')}</h2>
        <p className="text-muted-foreground">{t('Map each placeholder in your template to an employee, company, or custom field')}</p>
      </div>

      <div className="mb-6 p-4 bg-muted/40 rounded-lg border border-border">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-semibold text-foreground">{t('Mapping Progress')}</h4>
            <p className="text-sm text-muted-foreground">
              {t('{{mapped}} of {{total}} fields mapped', { mapped: mappedCount, total: fieldMappings.length })}
            </p>
          </div>
          <div className="flex items-center">
            <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all duration-300" style={{ width: `${progressPct}%` }} />
            </div>
            <span className="ms-3 text-sm font-medium text-foreground">{progressPct}%</span>
          </div>
        </div>
      </div>

      {fieldMappings.length === 0 && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
          {t('No placeholders were detected in this template. You can still save it as a draft.')}
        </div>
      )}

      <div className="space-y-4 mb-6">
        {fieldMappings?.map((mapping, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border transition-all ${
              mapping?.mappedTo ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-foreground mb-2 block">{t('Template Placeholder')}</label>
                <div className="flex items-center">
                  <div className="px-4 py-2 bg-card border border-border rounded-lg font-mono text-sm">
                    {'{{' + mapping?.placeholder + '}}'}
                  </div>
                  {mapping?.autoMapped && (
                    <span className="ms-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">{t('Auto-mapped')}</span>
                  )}
                </div>
              </div>

              <div className="flex items-center pt-8">
                <Icon name="ArrowRight" size={20} className="text-muted-foreground rtl:rotate-180" />
              </div>

              <div className="flex-1">
                <Select
                  label={t('Data Field')}
                  placeholder={t('Select a field')}
                  options={availableFields}
                  value={mapping?.mappedTo}
                  onChange={(value) => handleFieldMapping(mapping?.placeholder, value)}
                  searchable
                  required
                />
                {mapping?.mappedTo && fieldByValue[mapping.mappedTo] && (
                  <span className="mt-1.5 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                    {groupLabel(fieldByValue[mapping.mappedTo].group)}
                  </span>
                )}
              </div>

              <div className="pt-8">
                {mapping?.mappedTo ? (
                  <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={16} color="white" strokeWidth={3} />
                  </div>
                ) : (
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                    <Icon name="AlertCircle" size={16} color="white" />
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {unmappedFields?.length > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 me-3 flex-shrink-0 mt-0.5" />
          <div>
            <h5 className="font-medium text-amber-900 mb-1">{t('Unmapped Fields')}</h5>
            <p className="text-sm text-amber-700">
              {t('Please map all remaining fields to continue.')}
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" onClick={onPrev} disabled={busy}>
          <Icon name="ArrowLeft" size={16} className="me-2 rtl:rotate-180" />
          {t('Back to Upload')}
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => save(false)} disabled={busy} loading={busy}>
            {t('Save as draft')}
          </Button>
          <Button onClick={() => save(true)} disabled={!canPublish || busy} loading={busy} iconName="Send" iconPosition="right">
            {t('Save & publish')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FieldMappingStep;
